import React, { createContext, useContext, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '../services';
import { User } from '../types/models';
import type { 
  StudentProfileResponse, 
  TeacherProfileResponse, 
  OrganizationProfileResponse 
} from '@/types/api';

// Add normalization helper
const normalizeUser = (data: any): User => ({
  ...data,
  school_code: data.school_code ?? null,
  student_profile: data.student_profile ?? undefined,
  teacher_profile: data.teacher_profile ?? undefined,
  organization: data.organization ?? undefined,
  created_at: data.created_at ?? new Date().toISOString()
});

type ProfileUpdateRequest = Partial<StudentProfileResponse> | Partial<TeacherProfileResponse> | Partial<OrganizationProfileResponse>;

interface UserContextType {
  profile: User | null;
  loading: boolean;
  error: Error | null;
  updateProfile: (data: ProfileUpdateRequest) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<User | null>(user ? normalizeUser(user) : null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refreshProfile = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      let response;
      switch (user.role) {
        case 'student':
          response = await api.profile.getStudentProfile();
          break;
        case 'teacher':
          response = await api.profile.getTeacherProfile();
          break;
        case 'organization':
          response = await api.profile.getOrganizationProfile();
          break;
        default:
          throw new Error(`Unsupported user role: ${user.role}`);
      }
      setProfile(normalizeUser(response.data));
    } catch (error) {
      setError(error as Error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (data: ProfileUpdateRequest): Promise<void> => {
    if (!user) throw new Error('No user found');

    try {
      let response;
      switch (user.role) {
        case 'student':
          response = await api.profile.updateStudentProfile(data as Partial<StudentProfileResponse>);
          break;
        case 'teacher':
          response = await api.profile.updateTeacherProfile(data as Partial<TeacherProfileResponse>);
          break;
        case 'organization':
          response = await api.profile.updateOrganizationProfile(data as Partial<OrganizationProfileResponse>);
          break;
        default:
          throw new Error(`Unsupported user role: ${user.role}`);
      }
      setProfile(normalizeUser(response.data));
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  };

  return (
    <UserContext.Provider value={{ profile, loading, error, updateProfile, refreshProfile }}>
      {children}
    </UserContext.Provider>
  );
};